import { ProductDetailsService } from '@product-details/services/product-details.service';
import { AdditionalServicesService } from '@additional-services/services/additional-services.service';
import { CompleteOrderService } from '@complete-order/services/complete-order.service';
import { ViewOffersService } from '@view-offers/services/view-offers.service';
import {
  GAAdditionalServicesPageLoad,
  GAAdditionalServicesPopUpClose,
  GAAdditionalServicesPopUpShow,
  GAAdditionalServicesContinueClick,
  GAAdditionalServicesProtectionSelect,
  GAAdditionalServicesExtraSelect,
  GAAdditionalServicesToggleBenefits,
  GAAdditionalServicesRejectPolicy,
} from '@analytics/classes/ga.additional.services.class';
import { AnalyticsService } from '@analytics/services/analytics.service';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as fromAnalytics from '../actions';
import {
  tap,
  withLatestFrom,
  filter,
  switchMap,
  map,
  delay,
} from 'rxjs/operators';
import { of } from 'rxjs';

@Injectable()
export class GAAdditionalServicesEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly analyticsService: AnalyticsService,
    private readonly viewOffersService: ViewOffersService,
    public readonly productDetailsService: ProductDetailsService,
    private readonly additionalServicesService: AdditionalServicesService,
    private readonly completeOrderService: CompleteOrderService
  ) {}

  PageLoad$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromAnalytics.AdditionalServicesPageLoad),
        delay(500),
        withLatestFrom(
          this.additionalServicesService.itemOfferUpsell$,
          this.additionalServicesService.itemOfferError$
        ),
        switchMap(([, upsell, error]) =>
          upsell && !error
            ? this.additionalServicesService.itemOfferLoaded$
            : of(true)
        ),
        filter(Boolean),
        withLatestFrom(
          this.viewOffersService.offers$,
          this.productDetailsService.selected$,
          this.additionalServicesService.itemOffer$,
          this.completeOrderService.replacementCost$
        ),
        tap(([, offers, selected, itemOffer, replacementCost]) => {
          const gaData = new GAAdditionalServicesPageLoad(
            offers,
            selected,
            itemOffer,
            replacementCost
          ).gaData;
          this.analyticsService.eventEmitter(gaData);
        })
      ),
    { dispatch: false }
  );

  PopupClose$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromAnalytics.AdditionalServicesPopUpClose),
        map((action) => action.payload),
        withLatestFrom(
          this.viewOffersService.offers$,
          this.productDetailsService.selected$,
          this.additionalServicesService.itemOffer$,
          this.completeOrderService.replacementCost$
        ),
        tap(([payload, offers, selected, itemOffer, replacementCost]) => {
          const gaData = new GAAdditionalServicesPopUpClose(
            offers,
            selected,
            payload?.upsellPopupAction,
            itemOffer,
            replacementCost
          ).gaData;
          this.analyticsService.eventEmitter(gaData);
        })
      ),
    { dispatch: false }
  );

  PopupShow$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromAnalytics.AdditionalServicesPopUpShow),
        withLatestFrom(
          this.viewOffersService.offers$,
          this.productDetailsService.selected$,
          this.additionalServicesService.itemOffer$,
          this.completeOrderService.replacementCost$
        ),
        tap(([, offers, selected, itemOffer, replacementCost]) => {
          const gaData = new GAAdditionalServicesPopUpShow(
            offers,
            selected,
            itemOffer,
            replacementCost
          ).gaData;
          this.analyticsService.eventEmitter(gaData);
        })
      ),
    { dispatch: false }
  );

  ContinueClick$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromAnalytics.AdditionalServicesContinueClick),
        withLatestFrom(
          this.viewOffersService.offers$,
          this.productDetailsService.selected$,
          this.additionalServicesService.itemOffer$,
          this.completeOrderService.replacementCost$
        ),
        tap(([, offers, selected, itemOffer, cost]) => {
          const gaData = new GAAdditionalServicesContinueClick(
            offers,
            selected,
            itemOffer,
            cost
          ).gaData;
          this.analyticsService.eventEmitter(gaData);
        })
      ),
    { dispatch: false }
  );

  ProtectionSelect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromAnalytics.AdditionalServicesProtectionSelect),
        delay(200),
        withLatestFrom(
          this.viewOffersService.offers$,
          this.productDetailsService.selected$,
          this.additionalServicesService.itemOffer$,
          this.completeOrderService.replacementCost$,
          this.additionalServicesService.isProtectionSelected$
        ),
        filter(([, , , , , isProtectionSelected]) => isProtectionSelected),
        tap(([, offers, selected, itemOffer, replacementCost]) => {
          const gaData = new GAAdditionalServicesProtectionSelect(
            offers,
            selected,
            itemOffer,
            replacementCost
          ).gaData;
          this.analyticsService.eventEmitter(gaData);
        })
      ),
    { dispatch: false }
  );

  ExtraSelect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromAnalytics.AdditionalServicesExtraSelect),
        map((action) => action.payload),
        withLatestFrom(
          this.viewOffersService.offers$,
          this.productDetailsService.selected$,
          this.additionalServicesService.itemOffer$,
          this.completeOrderService.replacementCost$
        ),
        tap(([description, offers, selected, itemOffer, replacementCost]) => {
          const gaData = new GAAdditionalServicesExtraSelect(
            offers,
            selected,
            description,
            itemOffer,
            replacementCost
          ).gaData;
          this.analyticsService.eventEmitter(gaData);
        })
      ),
    { dispatch: false }
  );

  AdditionalServicesRejectNewPolicy$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromAnalytics.AdditionalServicesRejectNewPolicy),
        withLatestFrom(
          this.viewOffersService.offers$,
          this.productDetailsService.selected$,
          this.additionalServicesService.itemOffer$,
          this.completeOrderService.replacementCost$
        ),
        tap(([, offers, selected, itemOffer, replacementCost]) => {
          const gaData = new GAAdditionalServicesRejectPolicy(
            offers,
            selected,
            itemOffer,
            replacementCost
          ).gaData;
          this.analyticsService.eventEmitter(gaData);
        })
      ),
    { dispatch: false }
  );

  AdditionalServicesToggleBenefits$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromAnalytics.AdditionalServicesToggleBenefits),
        map((action) => action.payload),
        withLatestFrom(
          this.viewOffersService.offers$,
          this.productDetailsService.selected$,
          this.additionalServicesService.itemOffer$,
          this.completeOrderService.replacementCost$
        ),
        tap(([payload, offers, selected, itemOffer, replacementCost]) => {
          const gaData = new GAAdditionalServicesToggleBenefits(
            offers,
            selected,
            itemOffer,
            replacementCost,
            payload?.isOpen
          ).gaData;
          this.analyticsService.eventEmitter(gaData);
        })
      ),
    { dispatch: false }
  );
}
